import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  ALARM_ACTIVE,
  ALARM_OFF,
  ANNOUNCEMENT_ALARM_KEY,
  ANNOUNCEMENT_BLUECOIN_KEY,
  ANNOUNCEMENT_EVENT_KEY,
  ANNOUNCEMENT_WAYPOINT_KEY,
  BLUECOIN_ACTIVE,
  BLUECOIN_OFF,
  COMMENT_OFF,
  DAYS_KEY,
  EVENT_ACTIVE,
  EVENT_OFF,
  ICheckedTableItems,
  INFO_FILTER,
  IS_DAY_TIME,
  ITripsOverviewTableData,
  PURPOSE_ACTIVE,
  PURPOSE_OFF,
  RIDEBOOK_PURPOSE_KEY,
  TIME_KEY,
  WP_ACTIVE,
  WP_OFF,
} from '../interfaces';
import { COMMENT_KEY } from '../constants';
import {
  advancedFiltersAtom,
  checkedTableItemsAtom,
  hasMoreAtom,
  isActiveAdvanceFilterAtom,
  isModalFormOpenedAtom,
  offsetAtom,
  rideBookDataAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import { defineWeekdays } from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';

export default function AdvancedFilterChips(): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [advancedFilters, setAdvancedFilters] = useRecoilState(advancedFiltersAtom);
  const setIsModalFormOpened = useSetRecoilState<string>(isModalFormOpenedAtom);
  const [isActiveAdvanceFilter, setIsActiveAdvanceFilter] =
    useRecoilState(isActiveAdvanceFilterAtom);
  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const setCheckedTableItems = useSetRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const { trackEvent } = useAnalytics();

  const handleOpenFilter = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['ride book', 'click', 'Ride book advanced filter open']);
    const id = event.currentTarget.id;
    if (id === DAYS_KEY || id === TIME_KEY) {
      setIsModalFormOpened(IS_DAY_TIME);
    } else {
      setIsModalFormOpened(INFO_FILTER);
    }
  };

  const handleDaysValue = (): string => {
    const weekDays = defineWeekdays();

    if (!weekDays.weekdaysMin) {
      return '';
    }

    let tempWeekDays = weekDays.weekdaysMin.map((item, index) => ({
      name: item,
      indexOfDay: String(index),
    }));

    if (weekDays.name !== 'en') {
      const tempArray = [...tempWeekDays];
      tempWeekDays = tempArray.slice(1).concat(tempArray[0]);
    }

    if (advancedFilters.days.length > 6) {
      return `${f({ id: 'tables.tripsOverview.modal.dayTime.weekdays' })}: ${f({
        id: 'tables.tripsOverview.modal.dayTime.inall',
      })}`;
    }

    const selectedDays = tempWeekDays
      .filter(({ indexOfDay }) => advancedFilters.days.includes(indexOfDay))
      .map(({ name }) => name)
      .join(', ');

    return `${f({ id: 'tables.tripsOverview.modal.dayTime.weekdays' })}: ${selectedDays}`;
  };

  const resetState = () => {
    setOffset(0);
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setRideItemIds([]);
    setHasMore(true);
    setCheckedTableItems({
      mainCheckbox: false,
      intervals: [],
      intervalsBlacklist: [],
      rideIdBlacklist: {},
      rideIds: {},
      customerId: null,
      counts: {},
    });
  };

  const handleRemoveChips = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    const id = event.currentTarget.id;

    resetState();

    const filtersMap = {
      [DAYS_KEY]: {
        newFilter: {
          days: ['0', '1', '2', '3', '4', '5', '6'],
        },
        newActiveState: {
          days: false,
        },
      },
      [TIME_KEY]: {
        newFilter: {
          timeFrom: new Date(new Date().setHours(0, 0, 0, 0)),
          timeTo: new Date(new Date().setHours(23, 59, 59, 0)),
          closedInterval: false,
        },
        newActiveState: {
          time: false,
        },
      },
      [COMMENT_KEY]: {
        newFilter: {
          comment: false,
          commentChoice: COMMENT_OFF,
        },
        newActiveState: {
          comment: false,
        },
      },
      [RIDEBOOK_PURPOSE_KEY]: {
        newFilter: {
          ridebookPurpose: {},
          purposeChoice: PURPOSE_OFF,
          purposeChipName: null,
        },
        newActiveState: {
          ridebookPurpose: false,
        },
      },
      [ANNOUNCEMENT_EVENT_KEY]: {
        newFilter: {
          announcementEvent: [],
          eventChoice: EVENT_OFF,
          eventChipName: null,
        },
        newActiveState: {
          announcementEvent: false,
        },
      },
      [ANNOUNCEMENT_BLUECOIN_KEY]: {
        newFilter: {
          announcementBluecoin: [],
          bluecoinChoice: BLUECOIN_OFF,
          bluecoinChipName: null,
        },
        newActiveState: {
          announcementBluecoin: false,
        },
      },
      [ANNOUNCEMENT_ALARM_KEY]: {
        newFilter: {
          announcementAlarm: [],
          alarmChoice: ALARM_OFF,
          alarmChipName: null,
        },
        newActiveState: {
          announcementAlarm: false,
        },
      },
      [ANNOUNCEMENT_WAYPOINT_KEY]: {
        newFilter: {
          announcementWaypoint: [],
          waypointChoice: WP_OFF,
          waypointChipName: null,
        },
        newActiveState: {
          announcementWaypoint: false,
        },
      },
    };

    if (filtersMap[id]) {
      const { newFilter, newActiveState } = filtersMap[id];

      setAdvancedFilters((oldState) => ({ ...oldState, ...newFilter }));
      setIsActiveAdvanceFilter((oldState) => ({ ...oldState, ...newActiveState }));
    }
  };

  const handleTimeValues = (): string => {
    if (advancedFilters.closedInterval) {
      return `${f({ id: 'tables.tripsOverview.modal.dayTime.inallInterval' })}: ${dayjs(
        advancedFilters.timeFrom
      ).format('HH:mm')} - ${dayjs(advancedFilters.timeTo).format('HH:mm')}`;
    }
    return `${f({ id: 'tables.tripsOverview.modal.dayTime.run' })}: ${dayjs(
      advancedFilters.timeFrom
    ).format('HH:mm')} - ${dayjs(advancedFilters.timeTo).format('HH:mm')}`;
  };

  const handleChipName = (id: string): string => {
    switch (id) {
      case RIDEBOOK_PURPOSE_KEY:
        if (advancedFilters.purposeChoice === PURPOSE_ACTIVE) {
          return `${f({ id: 'tables.tripsOverview.modal.infoFilter.purposeActive' })}: ${
            advancedFilters.purposeChipName || ''
          }`;
        }
        return f({ id: 'tables.tripsOverview.modal.infoFilter.purposeWithout' });
      case ANNOUNCEMENT_EVENT_KEY:
        if (advancedFilters.eventChoice === EVENT_ACTIVE) {
          return `${f({ id: 'tables.tripsOverview.modal.infoFilter.eventActive' })}: ${
            advancedFilters.eventChipName || ''
          }`;
        }
        return f({ id: 'tables.tripsOverview.modal.infoFilter.eventWithout' });

      case ANNOUNCEMENT_BLUECOIN_KEY:
        if (advancedFilters.bluecoinChoice === BLUECOIN_ACTIVE) {
          return `${f({ id: 'tables.tripsOverview.modal.infoFilter.bluecoinActive' })}: ${
            advancedFilters.bluecoinChipName || ''
          }`;
        }
        return f({ id: 'tables.tripsOverview.modal.infoFilter.bluecoinWithout' });
      case ANNOUNCEMENT_ALARM_KEY:
        if (advancedFilters.alarmChoice === ALARM_ACTIVE) {
          return `${f({ id: 'tables.tripsOverview.modal.infoFilter.alarmActive' })}: ${
            advancedFilters.alarmChipName || ''
          }`;
        }
        return f({ id: 'tables.tripsOverview.modal.infoFilter.alarmWithout' });
      case ANNOUNCEMENT_WAYPOINT_KEY:
        if (advancedFilters.waypointChoice === WP_ACTIVE) {
          return `${f({ id: 'tables.tripsOverview.modal.infoFilter.wpActive' })}: ${
            advancedFilters.waypointChipName || ''
          }`;
        }
        return f({ id: 'tables.tripsOverview.modal.infoFilter.wpWithout' });

      default:
        if (advancedFilters.comment) {
          return f({ id: 'tables.tripsOverview.modal.infoFilter.commentActive' });
        }
        return f({ id: 'tables.tripsOverview.modal.infoFilter.commentWithout' });
    }
  };

  const chipsMap = [
    {
      id: DAYS_KEY,
      dataCy: 'ride-book-filter-chips-advanced-days',
      key: 'days-filters-days',
      valueHandler: handleDaysValue,
    },
    {
      id: TIME_KEY,
      dataCy: 'ride-book-filter-chips-advanced-time',
      key: 'days-filters-time',
      valueHandler: handleTimeValues,
    },
    {
      id: COMMENT_KEY,
      dataCy: 'ride-book-filter-chips-advanced-comment',
      key: 'info-filters-comment',
      valueHandler: handleChipName,
    },
    {
      id: RIDEBOOK_PURPOSE_KEY,
      dataCy: 'ride-book-filter-chips-advanced-purpose',
      key: 'info-filters-purpose',
      valueHandler: handleChipName,
    },
    {
      id: ANNOUNCEMENT_EVENT_KEY,
      dataCy: 'ride-book-filter-chips-advanced-event',
      key: 'info-filters-event',
      valueHandler: handleChipName,
    },
    {
      id: ANNOUNCEMENT_BLUECOIN_KEY,
      dataCy: 'ride-book-filter-chips-advanced-bluecoin',
      key: 'info-filters-bluecoin',
      valueHandler: handleChipName,
    },
    {
      id: ANNOUNCEMENT_ALARM_KEY,
      dataCy: 'ride-book-filter-chips-advanced-alarm',
      key: 'info-filters-alarm',
      valueHandler: handleChipName,
    },
    {
      id: ANNOUNCEMENT_WAYPOINT_KEY,
      dataCy: 'ride-book-filter-chips-advanced-waypoint',
      key: 'info-filters-waypoint',
      valueHandler: handleChipName,
    },
  ];

  return (
    <>
      {chipsMap.map((chip) =>
        isActiveAdvanceFilter[chip.id] ? (
          <div
            id={chip.id}
            className="label label-primary mr-1 mb-1 label--filter dropdown-toggle"
            onClick={handleOpenFilter}
            style={{
              position: 'relative',
              whiteSpace: 'nowrap',
              textAlign: 'left',
              cursor: 'pointer',
            }}
            data-cy={chip.dataCy}
            key={chip.key}
          >
            {chip.valueHandler(chip.id)}
            <i
              id={chip.id}
              className="cmd-icon cmd-icon-remove-sm"
              style={{
                paddingLeft: '8px',
                cursor: 'pointer',
              }}
              onClick={handleRemoveChips}
              data-cy="ride-book-filter-chips-close"
            />
          </div>
        ) : null
      )}
    </>
  );
}
